import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton, Popper } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import theme from "themeProvider";
import SafeBoundary from "../SafeBoundary";
import EventDetailsPanel from "./EventDetailsPanel";
import { DetailLink } from "./DetailParts";
import { DETAILS_CLOSE_DELAY_MS } from "./constants";

const POPPER_MODIFIERS = [
  { name: "offset", options: { offset: [0, 8] } },
  { name: "flip", enabled: true },
  { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
];

const cardLinkSx = {
  fontSize: 11,
  fontWeight: 700,
  color: theme.palette.primary.main,
};

// Card footer: main link + info icon that opens the details panel on hover/click.
const EventDetailsTrigger = ({ event, Icon, color, details }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const closeTimerRef = useRef(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  };

  useEffect(() => clearCloseTimer, []);

  const openDetails = useCallback((e) => {
    clearCloseTimer();
    const target = e.currentTarget;
    // Keep the icon as anchor when hovering into the panel itself.
    setAnchorEl((prev) => prev || target);
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(
      () => setAnchorEl(null),
      DETAILS_CLOSE_DELAY_MS,
    );
  }, []);

  const closeNow = useCallback(() => {
    clearCloseTimer();
    setAnchorEl(null);
  }, []);

  return (
    <Box display="flex" alignItems="center" gap={0.25}>
      {details.link && (
        <DetailLink link={details.link} fontSize={12} sx={cardLinkSx} />
      )}
      <IconButton
        size="small"
        onClick={openDetails}
        onMouseEnter={openDetails}
        onMouseLeave={scheduleClose}
        sx={{ p: 0.25, ml: "auto" }}
        aria-label="Event details"
      >
        <InfoOutlined sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
      </IconButton>
      <Popper
        open={!!anchorEl}
        anchorEl={anchorEl}
        placement="right-start"
        sx={{ zIndex: 1400 }}
        modifiers={POPPER_MODIFIERS}
      >
        <Box onMouseEnter={openDetails} onMouseLeave={scheduleClose}>
          <SafeBoundary name="EventDetailsPanel">
            <EventDetailsPanel
              event={event}
              Icon={Icon}
              color={color}
              details={details}
              onClose={closeNow}
            />
          </SafeBoundary>
        </Box>
      </Popper>
    </Box>
  );
};

export default memo(EventDetailsTrigger);
